import React, { useEffect, useRef, useState } from "react";
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Typography,
  Checkbox,
  Collapse,
  IconButton,
  TableSortLabel,
} from "@mui/material";
import { KeyboardArrowDown, KeyboardArrowRight } from "@mui/icons-material";
import CustomPagination from "./custom-pagination";
import typography from "../cssUtils/typography";
import { ColorsConstants } from "../cssUtils/constants";

/**
 * AppTable — Standardised table component for the entire application.
 *
 * Props
 * ─────────────────────────────────────────────────────────────────────────────
 * columns          {Array}    Required. Column definitions.
 *                             Each entry: { id, label, minWidth?, align?, render? }
 *                             • render(row, index) – optional custom cell renderer
 *
 * rows             {Array}    Required. Data rows.
 * rowKey           {string|Function}  Defaults to row.id then index.
 * loading          {boolean}  Shows spinner.
 * emptyMessage     {string}   Empty state text. Default: 'No match found'.
 * maxHeight        {number|string}  Default: 600.
 * stickyHeader     {boolean}  Default: true.
 *
 * // ── Pagination ──────────────────────────────────────────────────────────
 * count / page / rowsPerPage / rowsPerPageOptions / onPageChange / onRowsPerPageChange
 *
 * // ── Row interaction ─────────────────────────────────────────────────────
 * onRowClick       {Function} (row, event) => void
 * rowClickable     {boolean}
 *
 * // ── Selection ───────────────────────────────────────────────────────────
 * selectable / hasSelectedAll / onSelectAll / isRowSelected / onSelectRow
 *
 * // ── Styling overrides ───────────────────────────────────────────────────
 * paperSx / containerSx / headCellSx / bodyCellSx
 * rowSx    {Function|object}  (row, index) => sx  OR plain sx object.
 *
 * // ── Custom head ─────────────────────────────────────────────────────────
 * customHead       {ReactElement}  Full <TableHead> for multi-row / rowSpan / colSpan headers.
 * customHeadColSpan {number}       colSpan for loading/empty rows when customHead is used.
 *
 * // ── Collapsible rows ────────────────────────────────────────────────────
 * renderExpanded   {Function} (row, index) => ReactNode
 *                             When provided, each row gets a leading expand/collapse
 *                             IconButton and a hidden detail row rendered below it.
 *                             The detail row spans all columns.
 * expandedRowSx    {object}   Extra sx for the detail <TableRow>.
 * isRowExpandedByDefault {Function} (row) => boolean
 *                             Optional — rows that open expanded whenever `rows` changes.
 * isRowExpandable  {Function} (row) => boolean
 *                             Optional — return false to disable the expand button
 *                             for a specific row. Default: always expandable.
 */
const AppTable = ({
  // Data
  columns = [],
  rows = [],
  rowKey,
  loading = false,
  emptyMessage = "No match found",

  // Layout
  maxHeight = 1000,
  stickyHeader = true,
  hideMainScroll = false,//replace with true for hide the main page scollbar
  autoAdjustHeight = true,

  // Pagination
  count,
  page,
  rowsPerPage,
  rowsPerPageOptions = [20, 50, 100],
  onPageChange,
  onRowsPerPageChange,

  // Row interaction
  onRowClick,
  rowClickable,

  // Selection
  selectable = false,
  hasSelectedAll = false,
  onSelectAll,
  isRowSelected,
  onSelectRow,
  hideSelectAllWhenEmpty = false,

  // Style overrides
  paperSx,
  containerSx = {
    scrollbarWidth: "thin",
    scrollbarColor: "#C5CEDB #F3F6FB",
    "&::-webkit-scrollbar": { width: "8px" },
    "&::-webkit-scrollbar-track": { backgroundColor: "#F3F6FB", borderRadius: "8px" },
    "&::-webkit-scrollbar-thumb": { backgroundColor: "#C5CEDB", borderRadius: "8px" },
    "&::-webkit-scrollbar-thumb:hover": { backgroundColor: "#A8B4C7" },
  },
  headCellSx,
  bodyCellSx,
  rowSx,

  // Custom head
  customHead,
  customHeadColSpan,

  // Collapsible rows
  renderExpanded,
  expandedRowSx,
  isRowExpandable,
  isRowExpandedByDefault,

  // Sorting
  sortState = [],
  onSortChange,
}) => {
  const isClickable  = rowClickable ?? !!onRowClick;
  const isCollapsible = !!renderExpanded;
  const tablePaperRef = useRef(null);
  const paginationRef = useRef(null);

  // Internal expanded-row state — set of row keys
  const [expandedKeys, setExpandedKeys] = useState(new Set());
  const [dynamicMaxHeight, setDynamicMaxHeight] = useState(null);

  useEffect(() => {
    if (!isRowExpandedByDefault) return;
    setExpandedKeys(new Set(rows.map((row, index) => (isRowExpandedByDefault(row) ? getRowKey(row, index) : null)).filter((key) => key !== null)));
  }, [rows]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleExpanded = (key) => {
    setExpandedKeys((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  // ── Shared style tokens ───────────────────────────────────────────────────
  const BASE_HEAD_CELL_SX = {
    ...typography.bodyLarge,
    bgcolor: "#F9FAFB",
    color: "#1B2232",
    borderBottom: "1px solid #DCDFE5",
    whiteSpace: "nowrap",
    padding: "8px 12px",
    fontWeight: 400,
    position: "sticky",  // ← add
  top: 0,              // ← add
  zIndex: 10,
    ...headCellSx,
  };

  const BASE_BODY_CELL_SX = {
    ...typography.bodyLarge,
    fontWeight: 400,
    whiteSpace: "nowrap",
    borderBottom: "none",
    borderTop: "none",
    padding: "16px",
    ...bodyCellSx,
  };

  // ── Helpers ───────────────────────────────────────────────────────────────
  const getRowKey = (row, index) => {
    if (typeof rowKey === "function") return rowKey(row, index);
    if (typeof rowKey === "string")   return row[rowKey] ?? index;
    return row.id ?? index;
  };

  const getRowSx = (row, index) => {
    const alternating = {
      backgroundColor: index % 2 === 0 ? "#F0F5FF" : "#FFFFFF",
      "&:hover": { backgroundColor: index % 2 === 0 ? "#F5F5F5" : "#F7F9FC" },
      "& td": { borderBottom: "none", borderTop: "none" },
      cursor: isClickable ? "pointer" : "default",
    };
    const extra = typeof rowSx === "function" ? rowSx(row, index) : rowSx ?? {};
    return { ...alternating, ...extra };
  };

  const handleRowClick = (event, row) => {
    if (!onRowClick) return;
    if (
      event.target.closest('input[type="checkbox"]') ||
      event.target.closest("button") ||
      event.target.closest(".MuiIconButton-root")
    ) return;
    onRowClick(row, event);
  };

  const showPagination =
    count !== undefined &&
    page !== undefined &&
    rowsPerPage !== undefined &&
    onPageChange &&
    onRowsPerPageChange;
  const shouldShowSelectAllCheckbox =
    selectable && !(hideSelectAllWhenEmpty && rows.length === 0);

  // Total column count including optional expand + select columns
  const allColumns = selectable
    ? [{ id: "__select__", label: null, align: "center" }, ...columns]
    : columns;
  const expandColCount = isCollapsible ? 1 : 0;
  const totalColSpan   = allColumns.length + expandColCount;
  const emptyColSpan   = customHead
    ? (customHeadColSpan ?? totalColSpan)
    : totalColSpan;
  const shouldRenderPagination = showPagination && count > 20;
  const sanitizedContainerSx =
    autoAdjustHeight && containerSx
      ? (({ maxHeight: _maxHeightIgnored, height: _heightIgnored, ...rest }) => rest)(containerSx)
      : containerSx;

  useEffect(() => {
    if (!hideMainScroll || typeof document === "undefined") return;

    const html = document.documentElement;
    const body = document.body;
    const main = document.querySelector("main");
    const lockKey = "__appTableMainScrollLockCount";

    const count = window[lockKey] ?? 0;
    if (count === 0) {
      window.__appTablePrevHtmlOverflow = html.style.overflow;
      window.__appTablePrevBodyOverflow = body.style.overflow;
      window.__appTablePrevMainOverflow = main?.style?.overflow ?? "";
      html.style.overflow = "hidden";
      body.style.overflow = "hidden";
      if (main) main.style.overflow = "hidden";
    }
    window[lockKey] = count + 1;

    return () => {
      const nextCount = Math.max((window[lockKey] ?? 1) - 1, 0);
      window[lockKey] = nextCount;
      if (nextCount === 0) {
        html.style.overflow = window.__appTablePrevHtmlOverflow ?? "";
        body.style.overflow = window.__appTablePrevBodyOverflow ?? "";
        if (main) main.style.overflow = window.__appTablePrevMainOverflow ?? "";
      }
    };
  }, [hideMainScroll]);

  useEffect(() => {
    if (!autoAdjustHeight || typeof window === "undefined") {
      setDynamicMaxHeight(null);
      return;
    }

    const updateDynamicHeight = () => {
      const paperEl = tablePaperRef.current;
      if (!paperEl) return;

      const rect = paperEl.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const paginationHeight =
        shouldRenderPagination && paginationRef.current
          ? paginationRef.current.getBoundingClientRect().height
          : 0;

      // Stop above any <footer> element so the table doesn't overlap it.
      const footerEl = typeof document !== "undefined" ? document.querySelector("footer") : null;
      let bottomBound = viewportHeight;
      if (footerEl) {
        const footerMarginTop = parseFloat(window.getComputedStyle(footerEl).marginTop) || 0;
        bottomBound = footerEl.getBoundingClientRect().top - footerMarginTop;
      }

      const availableHeight = bottomBound - rect.top - paginationHeight - 24;
      setDynamicMaxHeight(Math.max(220, Math.floor(availableHeight)));
    };

    updateDynamicHeight();
    window.addEventListener("resize", updateDynamicHeight);
    return () => {
      window.removeEventListener("resize", updateDynamicHeight);
    };
  }, [autoAdjustHeight, shouldRenderPagination, count, page, rowsPerPage, rows.length]);

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <>
      <Paper
        ref={tablePaperRef}
        sx={{
          width: "100%",
          overflow: "hidden",
          // overflowX: "auto",
          border: "1px solid #DCDFE5",
          borderRadius: "8px",
          mb: 2,
          ...paperSx,
        }}
      >
 <TableContainer
          sx={{
            borderRadius: "8px",
            maxHeight: autoAdjustHeight ? (dynamicMaxHeight ?? maxHeight) : maxHeight,
            overflowY: "auto",
            overflowX: "auto",

            // Fixed layout settings to reliably hide the scrollbar engine on live builds
          /*   scrollbarWidth: "none",
            msOverflowStyle: "none",
            "&::-webkit-scrollbar": {
              display: "none !important",
              width: "0px !important",
              height: "0px !important",
            }, */
            ...sanitizedContainerSx,
          }}
        >
          <Table stickyHeader={stickyHeader} sx={{ minWidth: { xs: 600, md: 600 } }}>

            {/* ── Head ── */}
            {customHead ? customHead : (
              <TableHead>
                <TableRow>
                  {/* Expand toggle column header */}
                  {isCollapsible && (
                    <TableCell sx={{ ...BASE_HEAD_CELL_SX, width: 52, padding: "0 8px" }} />
                  )}

                  {/* Select column header */}
                  {selectable && (
                    <TableCell
                      align="center"
                      sx={{ ...BASE_HEAD_CELL_SX, padding: "0px" }}
                    >
                      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%" }}>
                        {shouldShowSelectAllCheckbox && (
                          <Checkbox
                            checked={hasSelectedAll}
                            onChange={onSelectAll}
                            sx={{
                              padding: 0,
                              "& .MuiSvgIcon-root": { fill: "#6C7C93" },
                              "&.Mui-checked .MuiSvgIcon-root": { fill: "#2563EB" },
                            }}
                          />
                        )}
                      </div>
                    </TableCell>
                  )}

                  {/* Data column headers */}
                  {columns.map((col) => (
                    <TableCell
                      key={col.id}
                      align={col.align ?? "left"}
                      style={{ minWidth: col.minWidth }}
                      sx={{ ...BASE_HEAD_CELL_SX }}
                    >
                      {col.sortable ? (() => {
                          const colSort = sortState.find((s) => s.id === col.id);
                          const sortIndex = sortState.findIndex((s) => s.id === col.id);
                          return (
                            <TableSortLabel
                              active={!!colSort}
                              direction={colSort?.direction ?? "asc"}
                              onClick={() => onSortChange?.(col.id)}
                              sx={{
                                ...typography.bodyLarge,
                                fontWeight: 400,
                                width: "100%",
                                justifyContent: "space-between",
                                color: ColorsConstants.primaryGray10,
                                "&.Mui-focusVisible, &:focus": {
                                  outline: "none",
                                  color: "#FFF !important"
                                },
                                "& .MuiTableSortLabel-icon": {
                                  color: `#FFF !important`,
                                  opacity: "0.8 !important",
                                },
                                "&:hover": {
                                  color: ColorsConstants.primaryBlue60,
                                  "& .MuiTableSortLabel-icon": {
                                    color: `${ColorsConstants.primaryBlue60} !important`,
                                    opacity: "1 !important"
                                  },
                                },
                                "&.Mui-active": {
                                  color: ColorsConstants.primaryBlue60,
                                  fontWeight: 600,
                                  "& .MuiTableSortLabel-icon": {
                                    color: `${ColorsConstants.primaryBlue60} !important`,
                                    opacity: "1 !important"
                                  },
                                },
                              }}
                            >
                              {col.label}
                              {sortState.length > 1 && sortIndex !== -1 && (
                                <span style={{
                                  marginLeft: 4,
                                  minWidth: 16,
                                  height: 16,
                                  borderRadius: "50%",
                                  backgroundColor: ColorsConstants.secondaryBackgroundColor,
                                  color: "#fff",
                                  fontSize: "10px",
                                  display: "inline-flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  flexShrink: 0,
                                  lineHeight: 1,
                                }}>
                                  {sortIndex + 1}
                                </span>
                              )}
                            </TableSortLabel>
                          );
                        })()
                      : (
                        <Typography sx={{ ...typography.bodyLarge, fontWeight: 400 }}>
                          {col.label}
                        </Typography>
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
            )}

            {/* ── Body ── */}
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={emptyColSpan} align="center" sx={{ py: 5 }}>
                    <CircularProgress size={30} />
                  </TableCell>
                </TableRow>
              ) : rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={emptyColSpan} align="center" sx={{ py: 5 }}>
                    <Typography sx={{ ...typography.bodyLarge, color: "text.secondary" }}>
                      {emptyMessage}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((row, index) => {
                  const key        = getRowKey(row, index);
                  const selected   = isRowSelected ? isRowSelected(row) : false;
                  const expandable = isRowExpandable ? isRowExpandable(row) : true;
                  const expanded   = expandedKeys.has(key);

                  return (
                    <React.Fragment key={key}>
                      {/* ── Main data row ── */}
                      <TableRow
                        hover={isClickable}
                        selected={selected}
                        sx={getRowSx(row, index)}
                        onClick={(e) => handleRowClick(e, row)}
                      >
                        {/* Expand toggle cell */}
                        {isCollapsible && (
                          <TableCell sx={{ ...BASE_BODY_CELL_SX, width: 52, padding: "0 8px" }}>
                            <IconButton
                              size="small"
                              disabled={!expandable}
                              onClick={(e) => { e.stopPropagation(); toggleExpanded(key); }}
                            >
                              {expanded
                                ? <KeyboardArrowDown fontSize="small" />
                                : <KeyboardArrowRight fontSize="small" />}
                            </IconButton>
                          </TableCell>
                        )}

                        {/* Checkbox cell */}
                        {selectable && (
                          <TableCell align="center" sx={{ ...BASE_BODY_CELL_SX }}>
                            <Checkbox
                              checked={hasSelectedAll || selected}
                              onChange={(e) => onSelectRow?.(e, row)}
                              disabled={hasSelectedAll}
                              sx={{
                                "& .MuiSvgIcon-root": { fill: "#6C7C93" },
                                "&.Mui-checked .MuiSvgIcon-root": { fill: "#2563EB" },
                              }}
                            />
                          </TableCell>
                        )}

                        {/* Data cells */}
                        {columns.map((col) => (
                          <TableCell
                            key={col.id}
                            align={col.align ?? "left"}
                            sx={{ ...BASE_BODY_CELL_SX }}
                          >
                            {col.render
                              ? col.render(row, index)
                              : (
                                <Typography sx={{ ...typography.bodyLarge, fontWeight: 400 }}>
                                  {row[col.id] ?? ""}
                                </Typography>
                              )}
                          </TableCell>
                        ))}
                      </TableRow>

                      {/* ── Expandable detail row ── */}
                      {isCollapsible && (
                        <TableRow sx={{ "& td": { borderBottom: "none", borderTop: "none" }, ...expandedRowSx }}>
                          <TableCell
                            colSpan={totalColSpan}
                            sx={{ p: 0, backgroundColor: "transparent" }}
                          >
                            <Collapse in={expanded} timeout="auto" unmountOnExit>
                              {renderExpanded(row, index)}
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  );
                })
              )}
            </TableBody>

          </Table>
        </TableContainer>
      {shouldRenderPagination && (
        <div ref={paginationRef}>
          <CustomPagination
            count={count}
            page={page}
            rowsPerPage={rowsPerPage}
            onPageChange={onPageChange}
            onRowsPerPageChange={onRowsPerPageChange}
            rowsPerPageOptions={rowsPerPageOptions}
          />
        </div>
      )}
      </Paper>

    </>
  );
};

export default AppTable;
